import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { ApiResponse } from '@/types'
import type { Vehicle, VehicleType, ParkerType } from '@/types/parking'

// Query keys
export const vehicleKeys = {
  all: ['vehicles'] as const,
  lists: () => [...vehicleKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...vehicleKeys.lists(), filters] as const,
  details: () => [...vehicleKeys.all, 'detail'] as const,
  detail: (id: number) => [...vehicleKeys.details(), id] as const,
  search: (licensePlate: string) => [...vehicleKeys.all, 'search', licensePlate] as const,
}

export interface VehicleFilters {
  page?: number
  vehicle_type?: VehicleType
  // Free-text search across plate/brand/model/color/province/customer. The
  // controller only reads `q` (Meilisearch), so the list search must send this.
  q?: string
  // Deprecated: the controller ignores `license_plate` on the list endpoint —
  // use `q` instead. The account-wide useVehicleSearch hook this was kept for
  // was removed (EP-116 T028, unreferenced + broken — the account-wide branch
  // never read this param either). Field stays only because useVehicles's own
  // regression test still guards against re-wiring it into the request params.
  license_plate?: string
  // EP-97: server-side sort (allow-listed keys: updated_at/created_at/license_plate/vehicle_type).
  sort?: string
  sort_order?: string
}

// Fetch vehicles for a property
export function useVehicles(propertyId: number, filters: VehicleFilters = {}) {
  return useQuery({
    queryKey: vehicleKeys.list({ propertyId, ...filters }),
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (filters.page) params.page = filters.page
      if (filters.vehicle_type) params.vehicle_type = filters.vehicle_type
      if (filters.q) params.q = filters.q
      if (filters.sort) params.sort = filters.sort
      if (filters.sort_order) params.sort_order = filters.sort_order
      const { data } = await api.get<ApiResponse<Vehicle[]>>(
        `/api/properties/${propertyId}/vehicles`,
        { params }
      )
      return data
    },
    enabled: !!propertyId,
  })
}

// EP-116 T028: an account-wide useVehicleSearch(licensePlate) hook used to
// live here — no consumer ever called it. It was also broken as written:
// Api::VehiclesController#index's account-wide branch (api/app/controllers/
// api/vehicles_controller.rb) only reads params[:q] (Meilisearch) or the bare
// unfiltered list — params[:license_plate] is never read there, so this hook
// would have silently returned the full unfiltered list regardless of the
// typed plate. No account-wide vehicle-search screen exists in the router;
// `q`-based Meilisearch search (see useVehicles above, and
// usePropertyVehicleSearch below) is the search mechanism that actually
// shipped. Removed 2026-08 (T028). vehicleKeys.search stays (harmless,
// independently unit-tested as a pure key builder).

// Fetch vehicles linked to a specific lease (EPV-11: "needs reassignment" section)
// Keyed under vehicleKeys.lists() so existing removeListQueries coverage includes it.
// parkerType: optional parker_type filter (e.g. 'tenant' for the TenantVehiclesCard)
export function useVehiclesByLease(leaseId: number, parkerType?: ParkerType) {
  return useQuery({
    queryKey: vehicleKeys.list({ leaseId, parkerType }),
    queryFn: async () => {
      const params: Record<string, unknown> = { lease_id: leaseId }
      if (parkerType) params.parker_type = parkerType
      const { data } = await api.get<ApiResponse<Vehicle[]>>('/api/vehicles', { params })
      return data
    },
    enabled: !!leaseId,
  })
}

// Fetch single vehicle
export function useVehicle(id: number) {
  return useQuery({
    queryKey: vehicleKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Vehicle>>(`/api/vehicles/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

// Create vehicle
export interface CreateVehicleInput {
  property_id: number
  license_plate: string
  vehicle_type: VehicleType
  province?: string
  brand?: string
  model?: string
  color?: string
  customer_id?: number
  notes?: string
  // P0-01: free-text plate label + registered photo keys
  plate_text?: string
  front_photo_key?: string
  rear_photo_key?: string
  // Tenant vehicle: associate with a specific lease + force parker_type
  lease_id?: number
  parker_type?: ParkerType
}

export function useCreateVehicle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ property_id, ...rest }: CreateVehicleInput) => {
      const { data } = await api.post<ApiResponse<Vehicle>>(
        `/api/properties/${property_id}/vehicles`,
        { vehicle: rest }
      )
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, vehicleKeys.lists())
    },
  })
}

// Update vehicle
export interface UpdateVehicleInput {
  id: number
  license_plate?: string
  vehicle_type?: VehicleType
  province?: string
  brand?: string
  model?: string
  color?: string
  customer_id?: number | null
  notes?: string
  // EPV-9/10/11: parker_type transitions (archive / reassign / convert-to-external)
  parker_type?: ParkerType
  lease_id?: number | null
}

export function useUpdateVehicle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateVehicleInput) => {
      const { data } = await api.patch<ApiResponse<Vehicle>>(`/api/vehicles/${id}`, {
        vehicle: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, vehicleKeys.lists())
      invalidateDetailQueries(queryClient, vehicleKeys.detail(data.id))
    },
  })
}

// Search vehicles within a property using the Meili ?q= param
// Enabled when propertyId is set and query is at least 2 chars.
export function usePropertyVehicleSearch(propertyId: number, query: string) {
  return useQuery({
    queryKey: vehicleKeys.list({ propertyId, q: query }),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Vehicle[]>>(
        `/api/properties/${propertyId}/vehicles`,
        { params: { q: query } }
      )
      return data
    },
    enabled: !!propertyId && query.trim().length >= 2,
    staleTime: 30_000,
  })
}

// Delete vehicle
export function useDeleteVehicle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/vehicles/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, vehicleKeys.lists())
    },
  })
}
